import React from 'react';
import PropTypes from 'prop-types';

import Tooltip from '../../../../library/tooltip/tooltip';
import { SPECS } from '../../product-specifications-constants';

import {
  getProductFitmentPosition,
  getProductSet
} from '../../../../shared/utils/product/product';
import {
  ProductProp,
  ProductTypeProp
} from '../../../../shared/utils/prop-types/prop-types';
import {
  isArrayWithLength,
  isNotEmptyNullOrUndefined
} from '../../../../shared/utils/validators/validators';

import { PRODUCT_TYPES } from '../../../../shared/constants/products';

import styles from './specs-table.scss';

function SpecsTable(props) {
  const { canonicalSpecs, product, productType, tooltips } = props;
  const { associatedSet, rearProduct } = product || {};
  const isSet = isArrayWithLength(associatedSet);
  const productSet = getProductSet(product);
  const productSetSlashDescription = productSet
    ?.map(product => getProductFitmentPosition(product, true))
    ?.join(' / ');
  const isStaggered = Boolean(rearProduct);
  const specs = SPECS[productType] || [];
  function getFormattedSpecValue(value, specInfo) {
    let formattedValue;
    const { id, unit = '' } = specInfo;

    const [valuePart1, valuePart2] = value?.toString().split(' - ') || [];

    if (!value) {
      formattedValue = '';
    } else if (valuePart2 && unit) {
      formattedValue = `${valuePart1}${unit} - ${valuePart2}${unit}`;
    } else if (id === 'tread.depth') {
      formattedValue = `${value}/32${unit}`;
    } else {
      formattedValue = `${value}${unit}`;
    }

    return formattedValue;
  }

  function getGradeValue(prop) {
    let overviewValue = product[prop] || 'N/A';

    if (canonicalSpecs?.[prop]) {
      overviewValue = canonicalSpecs[prop];
    }

    if (rearProduct?.[prop]) {
      overviewValue += ` / ${rearProduct[prop]}`;
    }

    return overviewValue;
  }

  function hasAccessoryDefaultSpecs(key, productProp) {
    return (
      productType === PRODUCT_TYPES.ACCESSORIES &&
      key === productProp &&
      (key === 'manufacturerAID' ||
        key === 'gtin' ||
        key === 'unitOfMeasureContents')
    );
  }

  return (
    <div className={styles['container']}>
      {productType === PRODUCT_TYPES.TIRES && (
        <>
          <h4>
            <span>UNIFORM TIRE QUALITY GRADE (UTQG)</span>
            <Tooltip
              className={styles['utqg-tooltip']}
              content="Uniform Tire Quality Grade"
            />
          </h4>
          <div className={styles['overview']}>
            <div>
              <span>Treadwear</span>
              <span>{getGradeValue('treadGrade')}</span>
            </div>
            <div>
              <span>Traction</span>
              <span>{getGradeValue('tractionGrade')}</span>
            </div>
            <div>
              <span>Temperature</span>
              <span>{getGradeValue('tempGrade')}</span>
            </div>
          </div>
        </>
      )}

      <table>
        <tbody>
          {specs.map((spec, idx) => {
            const doesProductSpecHaveValue = Object.keys(product).some(key => {
              if (hasAccessoryDefaultSpecs(key, spec.productProp)) {
                return true;
              } else {
                return (
                  key === spec.productProp &&
                  isNotEmptyNullOrUndefined(product[key])
                );
              }
            });

            const doesCanonicalSpecHaveValue =
              canonicalSpecs &&
              Object.keys(canonicalSpecs).some(key => {
                if (hasAccessoryDefaultSpecs(key, spec.productProp)) {
                  return true;
                } else {
                  return (
                    key === spec.productProp &&
                    isNotEmptyNullOrUndefined(canonicalSpecs[key])
                  );
                }
              });

            return doesProductSpecHaveValue || doesCanonicalSpecHaveValue ? (
              <tr key={spec.label}>
                <td>
                  {spec.label}
                  {tooltips?.[spec.id] ? (
                    <>
                      {' '}
                      <Tooltip
                        ariaLabel={`${spec.label} tooltip`}
                        content={tooltips[spec.id]}
                      />
                    </>
                  ) : null}
                </td>
                <td
                  data-before={
                    idx === 0 && isSet && productSetSlashDescription
                      ? productSetSlashDescription
                      : undefined
                  }
                >
                  {canonicalSpecs
                    ? getFormattedSpecValue(
                        canonicalSpecs[spec.productProp],
                        spec
                      )
                    : `${getFormattedSpecValue(
                        product[spec.productProp],
                        spec
                      )}${
                        isStaggered
                          ? ` / ${getFormattedSpecValue(
                              rearProduct[spec.productProp],
                              spec
                            )}`
                          : ''
                      }${
                        isSet
                          ? ` / ${product.associatedSet.map(associatedProduct =>
                              getFormattedSpecValue(
                                associatedProduct[spec.productProp],
                                spec
                              )
                            )}`
                          : ''
                      }`}
                </td>
              </tr>
            ) : null;
          })}
        </tbody>
      </table>
    </div>
  );
}

SpecsTable.propTypes = {
  canonicalSpecs: PropTypes.object,
  product: ProductProp.isRequired,
  productType: ProductTypeProp.isRequired,
  tooltips: PropTypes.object
};

export default SpecsTable;
